import {Button, Divider, Grid} from '@mantine/core';
import {createStyles} from '@mantine/core';
import React from 'react';

import {AntibodyDomainSearchInput} from 'antibodies/actions/AntibodyComponentSearchInput';
import {AntibodyComponentType} from 'antibodies/AntibodyConstants';
import AntibodyComponentSvg from 'antibodies/nested-data-creation/antibody-component-visualization/AntibodyComponentSvg';
import {TranslationParameters} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {AntibodyDomainInput} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import SearchForDnaPairsModal from 'antibodies/nested-data-creation/panes/SearchForDnaPairsModal';
import {pluralize} from 'BenchlingUtil';
import {CodonTranslationTable} from 'biojs/BioConstants';
import GeneticCodeDropdown from 'molbio/translations/GeneticCodeDropdown';
import getTestProps from 'test/lib/cypress/getTestProps';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import runModal from 'ui-library/runModal';

const ANTIBODY_COMPONENT_SVG_SIZE = 75;

const useStyles = createStyles((theme) => ({
  componentSection: {
    display: 'flex',
    gap: theme.spacing.lg,
    padding: '16px 0',

    h4: {
      marginBottom: theme.spacing.sm,
    },
  },
  componentSectionContent: {
    flexGrow: 1,
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: theme.spacing.xs,
  },
  aaDomainParamsContainer: {
    display: 'flex',
    gap: theme.spacing.lg,
  },
  geneticCode: {
    flexBasis: '33%',
  },
}));

export interface GetDomainsPaneProps {
  pairSchemaId: string;
  selectedPairIds: Array<string>;
  setSelectedPairIds: (selectedPairIds: Array<string>) => void;
  aaDomainTranslationParameters: TranslationParameters;
  updateAaDomainTranslationParameters: (translationParametersUpdate: Partial<TranslationParameters>) => void;
  constantAaDomainEntities: Array<AntibodyDomainInput>;
  selectConstantDomainEntity: (constantDomainEntity: AntibodyDomainInput) => void;
  registryId: string;
  constantDomainSchemaId: string;
}

/**
 * Nested data creation step that defines configuration for creating AA variable domains and chains
 * and selecting DNA variable domain pair entities.
 */
export default function GetDomainsPane({
  pairSchemaId,
  selectedPairIds,
  setSelectedPairIds,
  aaDomainTranslationParameters,
  updateAaDomainTranslationParameters,
  constantAaDomainEntities,
  selectConstantDomainEntity,
  registryId,
  constantDomainSchemaId,
}: GetDomainsPaneProps): JSX.Element {
  const {classes} = useStyles();

  async function openSearchModal(): Promise<void> {
    const newSelectedPairIds = await runModal(SearchForDnaPairsModal, {registryId, pairSchemaId});
    if (newSelectedPairIds != null) {
      setSelectedPairIds(newSelectedPairIds);
    }
  }

  return (
    <div>
      <div className={classes.componentSection}>
        <AntibodyComponentSvg
          componentType={AntibodyComponentType.DNA_DOMAIN}
          size={ANTIBODY_COMPONENT_SVG_SIZE}
        />
        <div>
          <h4 className={classes.sectionTitle}>DNA variable domains</h4>
          <p>Bring in DNA variable domains by selecting pair entities</p>
          {selectedPairIds.length === 0 ? (
            <Button
              leftIcon={<BenchlingIcon icon="magnifying-glass" />}
              onClick={openSearchModal}
              size="md"
              variant="tertiary"
            >
              Search for pair entities
            </Button>
          ) : (
            <Chip
              size={ChipSize.SMALL}
              text={`${selectedPairIds.length} ${pluralize(selectedPairIds.length, 'pair')} selected`}
              onRemove={() => {
                setSelectedPairIds([]);
              }}
            />
          )}
        </div>
      </div>
      <Divider my="md" />
      <div className={classes.componentSection}>
        <AntibodyComponentSvg
          componentType={AntibodyComponentType.AA_DOMAIN}
          size={ANTIBODY_COMPONENT_SVG_SIZE}
        />
        <div className={classes.componentSectionContent}>
          <h4 className={classes.sectionTitle}>AA variable domains</h4>
          <p>Configure parameters for translating DNA domains to AA domains</p>
          <div className={classes.aaDomainParamsContainer}>
            <div className={classes.geneticCode}>
              <FieldLabel label="Genetic code" />
              <GeneticCodeDropdown
                geneticCode={aaDomainTranslationParameters.geneticCode}
                onSelectGeneticCode={(geneticCode: CodonTranslationTable) => {
                  updateAaDomainTranslationParameters({geneticCode});
                }}
                disabled={false}
              />
            </div>
          </div>
        </div>
      </div>
      <Divider my="md" />
      <div
        className={classes.componentSection}
        {...getTestProps({component: 'GetDomainsPane', element: 'aaChainsSection'})}
      >
        <AntibodyComponentSvg
          componentType={AntibodyComponentType.AA_CHAIN}
          size={ANTIBODY_COMPONENT_SVG_SIZE}
        />
        <div className={classes.componentSectionContent}>
          <h4 className={classes.sectionTitle}>AA chains</h4>
          <p>Set constant domains for AA chains by selecting existing entities</p>
          <Grid>
            {constantAaDomainEntities.map((constantDomainEntity) => {
              const distinctDomainKey = constantDomainEntity.componentFormatKey.label;
              return (
                <Grid.Col span={4} key={distinctDomainKey}>
                  <AntibodyDomainSearchInput
                    key={distinctDomainKey}
                    disabled={false}
                    distinctDomain={constantDomainEntity.componentFormatKey}
                    selectedProtein={
                      (constantDomainEntity.aaComponent &&
                        constantDomainEntity.aaComponent.type === 'EXISTING' &&
                        constantDomainEntity.aaComponent.aaSequenceId) ||
                      null
                    }
                    setSelectedProtein={(newSelectedProteinId) => {
                      selectConstantDomainEntity({
                        ...constantDomainEntity,
                        aaComponent: newSelectedProteinId
                          ? {type: 'EXISTING', aaSequenceId: newSelectedProteinId}
                          : null,
                      });
                    }}
                    linkedSchemaId={constantDomainSchemaId}
                    showSwatch={false}
                    registryId={registryId}
                  />
                </Grid.Col>
              );
            })}
          </Grid>
        </div>
      </div>
    </div>
  );
}
